import { Injectable } from '@angular/core'
import { arrayUnion, doc, Firestore, serverTimestamp, Timestamp, updateDoc } from '@angular/fire/firestore'
import { SubscriptionEventActor, SubscriptionEventType, SubscriptionFieldChange, subscriptionsPath } from '@parkupp/core'
import * as moment from 'moment'
import { AuthenticationService } from './authentication.service'
import { ClientService } from './client.service'

export interface SubscriptionActorDetails {
    name?: string
    email?: string
    adminKey?: string
}

export interface EventOptions {
    previousStatus?: string
    newStatus?: string
    actorDetails?: SubscriptionActorDetails
    source?: string
    changes?: SubscriptionFieldChange[]
    metadata?: Record<string, any>
}

export interface TrackedSubscriptionField {
    /** Dot path into the Subscription, e.g. `plan.feeInCents` */
    path: string
    label: string
    format?: (value: any) => string
}

const formatCents = (value: any) => (value === null || value === undefined || value === '' ? '—' : `R${Number(value) / 100}`)

/**
 * The fields a subscription edit is diffed against. Anything not listed here is either
 * derived (denormalised names, timestamps) or internal bookkeeping.
 */
export const TRACKED_SUBSCRIPTION_FIELDS: TrackedSubscriptionField[] = [
    { path: 'type', label: 'Payment Mechanism' },
    { path: 'status', label: 'Status' },
    { path: 'startDate', label: 'Start Date' },
    { path: 'endDate', label: 'End Date' },
    { path: 'nextDebitDate', label: 'Next Invoice Date' },
    { path: 'period', label: 'Duration (months)' },
    { path: 'baysBooked', label: 'Bays' },
    { path: 'plan.feeInCents', label: 'Monthly Fee', format: formatCents },
    { path: 'plan.term', label: 'Billing Term' },
    { path: 'clientRef', label: 'Client' },
    { path: 'merchantRef', label: 'Merchant' },
    { path: 'parkingRef', label: 'Parking' },
    { path: 'paymentMethodRef', label: 'Payment Card' },
    { path: 'manualClientName', label: 'Manual Client Name' },
    { path: 'manualMerchantName', label: 'Manual Merchant Name' },
    { path: 'manualParkingName', label: 'Manual Parking Name' },
    { path: 'bayNumber', label: 'Bay Number' },
    { path: 'accessTagNumber', label: 'Access Tag Number' },
    { path: 'isMonthToMonth', label: 'Month-to-Month' },
    { path: 'm2mCancellationPending', label: 'Cancellation Pending' },
    { path: 'renewalStatus', label: 'Renewal Status' },
    { path: 'createdAt', label: 'Created Date' },
    { path: 'denormEnterpriseKey', label: 'Enterprise' },
]

/**
 * Every subscription change — by a client, a merchant or an admin — is appended to the
 * subscription's `eventLog` so the admin timeline can always answer "who changed what,
 * when, and from what to what".
 *
 * Writes use `arrayUnion` rather than a whole-array set, so a stale in-memory copy of the
 * log can never wipe out history written by someone else.
 */
@Injectable({
    providedIn: 'root',
})
export class SubscriptionEventLogService {
    constructor(private firestore: Firestore, private authService: AuthenticationService, private clientService: ClientService) {}

    /**
     * Build the plain event object without writing it. Use this when you already have an
     * `updateDoc` of your own: folding `eventLog: arrayUnion(event)` into that same write
     * keeps the change and its audit entry atomic, and tells the backend listener the change
     * is already attributed.
     */
    async buildEvent(
        eventType: SubscriptionEventType,
        description: string,
        actor: SubscriptionEventActor,
        options?: EventOptions
    ): Promise<Record<string, any>> {
        const actorDetails = options?.actorDetails ?? (await this.resolveActorDetails(actor))

        const event: Record<string, any> = {
            timestamp: Timestamp.now(),
            eventType,
            description,
            actor,
        }
        if (options?.previousStatus) {
            event['previousStatus'] = options.previousStatus
        }
        if (options?.newStatus) {
            event['newStatus'] = options.newStatus
        }
        if (actorDetails && Object.keys(actorDetails).length > 0) {
            event['actorDetails'] = actorDetails
        }
        if (options?.source) {
            event['source'] = options.source
        }
        if (options?.changes && options.changes.length > 0) {
            event['changes'] = options.changes
        }
        if (options?.metadata) {
            event['metadata'] = options.metadata
        }

        return event
    }

    /**
     * Build the event describing a field-level edit, ready to fold into your own write.
     * Returns null when nothing actually changed, so the timeline does not fill up with
     * empty "saved" entries.
     */
    async buildChangeEvent(
        actor: SubscriptionEventActor,
        changes: SubscriptionFieldChange[],
        options?: { source?: string; actorDetails?: SubscriptionActorDetails; summary?: string; metadata?: Record<string, any>; eventType?: SubscriptionEventType }
    ): Promise<Record<string, any> | null> {
        if (!changes || changes.length === 0) {
            return null
        }

        const actorDetails = options?.actorDetails ?? (await this.resolveActorDetails(actor))
        const who = actorDetails?.name || actorDetails?.email || this.actorLabel(actor)
        const summary = options?.summary ?? `${who} updated ${changes.map((change) => change.label).join(', ')}`

        return this.buildEvent(options?.eventType ?? this.updateEventTypeFor(actor), summary, actor, {
            actorDetails,
            source: options?.source,
            changes,
            metadata: options?.metadata,
        })
    }

    /**
     * Append an event to the subscription's log as a standalone write. Logging failures are
     * swallowed — an audit write must never break the user action it is describing.
     */
    async logEvent(
        subscriptionKey: string,
        eventType: SubscriptionEventType,
        description: string,
        actor: SubscriptionEventActor,
        options?: EventOptions
    ): Promise<void> {
        try {
            const event = await this.buildEvent(eventType, description, actor, options)
            await updateDoc(doc(this.firestore, subscriptionsPath(), subscriptionKey), {
                eventLog: arrayUnion(event),
                updatedAt: serverTimestamp(),
            })
        } catch (error) {
            console.error('[SubscriptionEventLog] Failed to log event', eventType, 'for', subscriptionKey, error)
        }
    }

    /**
     * Log a field-level edit. No-ops when nothing actually changed so the timeline does not
     * fill up with empty "saved" entries.
     */
    async logChanges(
        subscriptionKey: string,
        actor: SubscriptionEventActor,
        changes: SubscriptionFieldChange[],
        options?: {
            source?: string
            actorDetails?: SubscriptionActorDetails
            summary?: string
            metadata?: Record<string, any>
            eventType?: SubscriptionEventType
        }
    ): Promise<void> {
        try {
            const event = await this.buildChangeEvent(actor, changes, options)
            if (!event) {
                return
            }
            await updateDoc(doc(this.firestore, subscriptionsPath(), subscriptionKey), {
                eventLog: arrayUnion(event),
                updatedAt: serverTimestamp(),
            })
        } catch (error) {
            console.error('[SubscriptionEventLog] Failed to log changes for', subscriptionKey, error)
        }
    }

    /**
     * Read every tracked field off a subscription and format it for display. Take one of
     * these before mutating a subscription in place — the formatted values are detached
     * from the source object, so the "before" side of a diff can't shift under you.
     */
    snapshot(source: any, fields: TrackedSubscriptionField[] = TRACKED_SUBSCRIPTION_FIELDS): Record<string, string> {
        const snapshot: Record<string, string> = {}
        for (const field of fields) {
            const formatter = field.format ?? this.formatValue.bind(this)
            snapshot[field.path] = formatter(this.valueAtPath(source, field.path))
        }
        return snapshot
    }

    /** Diff two snapshots taken with {@link snapshot}. */
    diffSnapshots(before: Record<string, string>, after: Record<string, string>, fields: TrackedSubscriptionField[] = TRACKED_SUBSCRIPTION_FIELDS): SubscriptionFieldChange[] {
        const changes: SubscriptionFieldChange[] = []
        for (const field of fields) {
            if (before[field.path] !== after[field.path]) {
                changes.push({ field: field.path, label: field.label, from: before[field.path] ?? '—', to: after[field.path] ?? '—' })
            }
        }
        return changes
    }

    /**
     * Diff two subscription objects. Only safe when neither side is mutated between the
     * two reads — otherwise take a {@link snapshot} first.
     */
    buildChanges(before: any, after: any, fields: TrackedSubscriptionField[] = TRACKED_SUBSCRIPTION_FIELDS): SubscriptionFieldChange[] {
        return this.diffSnapshots(this.snapshot(before, fields), this.snapshot(after, fields), fields)
    }

    /** Build a one-field change without having to assemble two full snapshots. */
    singleChange(field: string, label: string, from: any, to: any, format?: (value: any) => string): SubscriptionFieldChange[] {
        const formatter = format ?? this.formatValue.bind(this)
        const formattedFrom = formatter(from)
        const formattedTo = formatter(to)
        if (formattedFrom === formattedTo) {
            return []
        }
        return [{ field, label, from: formattedFrom, to: formattedTo }]
    }

    /**
     * Best-effort identity of whoever is signed in. Falls back to the bare uid when the
     * profile lookup fails — an unnamed actor is better than an unlogged one.
     */
    async resolveActorDetails(actor: SubscriptionEventActor): Promise<SubscriptionActorDetails> {
        const details: SubscriptionActorDetails = {}

        const uid = this.authService.uid
        if (!uid) {
            return details
        }

        if (actor === SubscriptionEventActor.ADMIN) {
            details.adminKey = uid
        }

        const email = this.authService.email
        if (email) {
            details.email = email
        }

        try {
            const profile = this.clientService.activeClient?.$key === uid ? this.clientService.activeClient : await this.clientService.getClientByUid(uid)
            const name = `${profile?.firstName || ''} ${profile?.lastName || ''}`.trim()
            if (name) {
                details.name = name
            }
        } catch (error) {
            console.warn('[SubscriptionEventLog] Could not resolve actor profile', error)
        }

        return details
    }

    private updateEventTypeFor(actor: SubscriptionEventActor): SubscriptionEventType {
        if (actor === SubscriptionEventActor.ADMIN) {
            return SubscriptionEventType.ADMIN_UPDATED
        }
        if (actor === SubscriptionEventActor.MERCHANT) {
            return SubscriptionEventType.MERCHANT_UPDATED
        }
        if (actor === SubscriptionEventActor.CLIENT) {
            return SubscriptionEventType.CLIENT_UPDATED
        }
        return SubscriptionEventType.OTHER
    }

    private actorLabel(actor: SubscriptionEventActor): string {
        if (actor === SubscriptionEventActor.ADMIN) {
            return 'Admin'
        }
        if (actor === SubscriptionEventActor.MERCHANT) {
            return 'Merchant'
        }
        if (actor === SubscriptionEventActor.CLIENT) {
            return 'Client'
        }
        return 'System'
    }

    private valueAtPath(source: any, path: string): any {
        return path.split('.').reduce((value, key) => (value === null || value === undefined ? value : value[key]), source)
    }

    private formatValue(value: any): string {
        if (value === null || value === undefined || value === '') {
            return '—'
        }
        if (typeof value === 'boolean') {
            return value ? 'Yes' : 'No'
        }
        if (typeof value === 'object') {
            // Firestore Timestamp (SDK instance or the plain { seconds } shape read back off a doc)
            if (typeof value.toDate === 'function') {
                return moment(value.toDate()).format('D MMM YYYY')
            }
            if (typeof value.seconds === 'number') {
                return moment(new Date(value.seconds * 1000)).format('D MMM YYYY')
            }
            // DocumentReference
            if (typeof value.id === 'string') {
                return value.id
            }
            return JSON.stringify(value)
        }
        return String(value)
    }
}
